import { makeStyles } from '@material-ui/styles'
import { RootState, useAppSelector } from 'app/store'
import StepperModalForm, { ModalStep } from 'core/elements/modal/StepperModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isAirgapped } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { IClusterDetailTabs } from 'k8s/components/dashboard/ClusterDetailTabs'
import { createKaapiClusterAddonBody } from 'k8s/components/kaapi/cluster-addons/action-helpers'
import { createKaapiClusterAddon } from 'k8s/components/kaapi/cluster-addons/actions'
import { createKaapiClusterBody } from 'k8s/components/kaapi/clusters/action-helpers'
import { createKaapiCluster } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersSelector } from 'k8s/components/kaapi/clusters/selectors'
import { listKaapiConfigMaps } from 'k8s/components/kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import { createHostedControlPlaneBody } from 'k8s/components/kaapi/hosted-control-plane/action-helpers'
import { createKaapiHostedControlPlane } from 'k8s/components/kaapi/hosted-control-plane/actions'
import { createKaapiKubeadmConfigTemplateBody } from 'k8s/components/kaapi/kubeadm-config-templates/action-helpers'
import { createKaapiKubeadmConfigTemplate } from 'k8s/components/kaapi/kubeadm-config-templates/actions'
import { createKaapiMachineDeploymentBody } from 'k8s/components/kaapi/machine-deployment/action-helpers'
import { createKaapiMachineDeployment } from 'k8s/components/kaapi/machine-deployment/actions'
import { createKaapiOpenStackClusterBody } from 'k8s/components/kaapi/openstack-clusters/action-helpers'
import { createKaapiOpenStackCluster } from 'k8s/components/kaapi/openstack-clusters/actions'
import { createKaapiOpenStackMachineTemplateBody } from 'k8s/components/kaapi/openstack-machine-templates/action-helpers'
import { createKaapiOpenStackMachineTemplate } from 'k8s/components/kaapi/openstack-machine-templates/actions'
import {
  ApplicationCredentialType,
  createKaapiApplicationCredentials,
  ensureIdentityRefSecret,
  listKaapiSecrets,
} from 'k8s/components/kaapi/secrets/actions'
import ValidatedForm from 'pf9-ui-components/built/components/validatedForm/ValidatedForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { isNilOrEmpty } from 'utils/fp'
import ClusterArchitectureFieldsOpenStack from './form-components/ClusterArchitectureFieldsOpenStack'
import ClusterConfigFields from './form-components/ClusterConfigFields'
import ClusterNameField from './form-components/ClusterNameField'
import { getDUInternalIP } from './form-components/helpers'
import ServerConfigurationFields, {
  NodePoolErrors,
} from './form-components/ServerConfigurationFields'
import ServiceUnhealthyInfo from 'app/plugins/openstack/components/common/ServiceUnhealthyInfo'
import { v4 as uuidv4 } from 'uuid'
import { kaapiSecretsSelector } from 'k8s/components/kaapi/secrets/selectors'

const validateNodePools = (nodePools) => {
  const errors = {}
  nodePools.forEach(({ id, flavor, replicas }) => {
    if (!flavor || !replicas) {
      errors[id] = errors[id] || {}
      if (!flavor) {
        errors[id]['flavor'] = 'A VM flavor selection is required.'
      }
      if (!replicas) {
        errors[id]['replicas'] = 'The value must be greater or equal than 1'
      }
    }
  })
  return errors
}

const initialValues = {
  nodePools: [{ id: 1 }],
  calico: true,
  coreDns: true,
  enableAddons: true,
  controlPlaneType: 'managed',
  apiServerFlags: '',
  schedulerFlags: '',
  controllerManagerFlags: '',
  metallb: false,
  openstackCluster: '',
  image: '',
  gpuAddons: false,
}

/**
 * @summary - This component is used to deploy a k8s workload cluster for OpenStack infrastructure
 * @flow
 * 1. Cluster Infrastructure:
 *    - User selects Virtualized Cluster from the radio group
 * 2. Cluster Architecture:
 *    - User selects a tenant
 *    - User selects the control plane type and its configuration
 *    - user selects the virtulize cluster and ssh key
 * 3. Node Pools:
 *    - User selects a flavor, enters the number of replicas
 *    - user select storage pool, network and subnet
 * 4. Cluster Configuration:
 *    - User select k8s version
 *    - User adds k8s flags (optional)
 *    - User selects the addons
 * 5. Final Touches -
 *    - Cluster Name: User enters the cluster name
 * 6. User clicks the "Add Cluster" button - handleOpenStackClusterCreation will be called
 *    - validate the form - node pools, image, ssh key, cluster name
 *    - We have added error handling after each API call
 *    - Create IdentityRefSecret (optional)
 *    - Create ClusterConfigSecret object
 *    - Create OpenStackCluster object
 *    - Create HostedControlPlane object
 *    - Create Cluster object
 *    For Each Node Pool
 *      - Create KubeadmConfigTemplate object
 *      - Create OpenStackMachineTemplate object
 *      - Create OpenStackMachineDeployment object
 *    - Create ClusterAddons object
 * On success, the user will be redirected to the cluster detail page
 */
const ClusterDeploymentForm = ({ onClose }) => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)

  const { updatePrefs: updateK8sPluginGlobalParams } = useScopedPreferences('k8sPluginGlobalParams')
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [nodePoolErrors, setNodePoolErrors] = useState<NodePoolErrors>({})
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isGPUFlavorSelected, setIsGPUFlavorSelected] = useState(false)

  const isAirgappedEnv = isAirgapped()

  // Load Clusters
  // const { loading: loadingClusters } = useListAction(listKaapiClusters)
  const clusters = useAppSelector(kaapiClustersSelector)

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })

  useListAction(listKaapiSecrets)
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const secrets = useSelector(kaapiSecretsSelector)
  const duInternalIP = getDUInternalIP(configMaps)

  // Create Actions
  const {
    update: createOpenStackCluster,
    updating: creatingOpenStackCluster,
    error: createOpenStackClusterError,
  } = useUpdateAction(createKaapiOpenStackCluster)
  const {
    update: createHostedControlPlane,
    updating: creatingHostedControlPlane,
    error: createHostedControlPlaneError,
  } = useUpdateAction(createKaapiHostedControlPlane)
  const {
    update: createCluster,
    updating: creatingCluster,
    error: createClusterError,
  } = useUpdateAction(createKaapiCluster)
  const {
    update: createKubeadmConfigTemplate,
    updating: creatingKubeadmConfigTemplate,
    error: createKubeadmConfigTemplateError,
  } = useUpdateAction(createKaapiKubeadmConfigTemplate)
  const {
    update: createOpenStackMachineTemplate,
    updating: creatingOpenStackMachineTemplate,
    error: createOpenStackMachineTemplateError,
  } = useUpdateAction(createKaapiOpenStackMachineTemplate)
  const {
    update: createMachineDeployment,
    updating: creatingMachindeDeployment,
    error: createMachineDeploymentError,
  } = useUpdateAction(createKaapiMachineDeployment)
  const {
    update: createClusterAddon,
    updating: creatingClusterAddon,
    error: createClusterAddonError,
  } = useUpdateAction(createKaapiClusterAddon)

  const hasError =
    !!createHostedControlPlaneError ||
    !!createOpenStackClusterError ||
    !!createClusterError ||
    !!createKubeadmConfigTemplateError ||
    !!createOpenStackMachineTemplateError ||
    !!createMachineDeploymentError ||
    !!createClusterAddonError

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const error =
      createHostedControlPlaneError ||
      createOpenStackClusterError ||
      createClusterError ||
      createKubeadmConfigTemplateError ||
      createOpenStackMachineTemplateError ||
      createMachineDeploymentError ||
      createClusterAddonError

    setError({
      title: error?.title || 'Error creating cluster',
      message: error?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [
    hasError,
    createHostedControlPlaneError,
    createOpenStackClusterError,
    createClusterError,
    createKubeadmConfigTemplateError,
    createOpenStackMachineTemplateError,
    createMachineDeploymentError,
    createClusterAddonError,
  ])

  // ---- Validation ----
  const validateClusterParams = (params) => {
    const nodePoolErrors = validateNodePools(params.nodePools)
    setNodePoolErrors(nodePoolErrors)
    if (!isNilOrEmpty(nodePoolErrors) || !params?.image || !params?.openstackCluster) {
      return false
    }
    return true
  }

  // ---- Cluster Secret ----
  const createClusterSecret = (params) =>
    createKaapiApplicationCredentials(
      ApplicationCredentialType.Cluster,
      params.clusterName,
      !params.metallb,
    )

  // ---- Infrastructure ----
  const createClusterInfrastructure = async (params, namespace) => {
    // OpenStackCluster
    const oscBody = createKaapiOpenStackClusterBody({
      name: params.clusterName,
      namespace,
      network: params.nodePools[0].network,
      subnet: params.nodePools[0].subnet,
      openstackCluster: params.openstackCluster,
    })
    const { success: oscSuccess, error: oscError } = await createOpenStackCluster({
      namespace,
      body: oscBody,
    })
    if (!oscSuccess)
      return { success: false, error: oscError, message: 'Failed to create OpenStackCluster' }

    // HostedControlPlane
    const hcpBody = createHostedControlPlaneBody({
      name: `${params.clusterName}cp`,
      namespace,
      version: params.k8sVersion,
      apiServerFlags: params.apiServerFlags ? params.apiServerFlags.split(',') : [],
      schedulerFlags: params.schedulerFlags ? params.schedulerFlags.split(',') : [],
      controllerManagerFlags: params.controllerManagerFlags
        ? params.controllerManagerFlags.split(',')
        : [],
      clusterName: params.clusterName,
    })
    const { success: hcpSuccess, error: hcpError } = await createHostedControlPlane({
      namespace,
      body: hcpBody,
    })
    if (!hcpSuccess)
      return { success: false, error: hcpError, message: 'Failed to create HostedControlPlane' }

    // Cluster
    const clusterBody = createKaapiClusterBody({
      name: params.clusterName,
      namespace,
      hostedControlPlaneRefName: `${params.clusterName}cp`,
      infrastructureRefName: params.clusterName,
      version: params.k8sVersion,
      gpuAddons: params.gpuAddons,
    })
    const { success: cSuccess, error: cError } = await createCluster({
      namespace,
      body: clusterBody,
    })
    if (!cSuccess) return { success: false, error: cError, message: 'Failed to create Cluster' }

    return { success: true }
  }

  // ---- Config Templates ----
  const createConfigTemplates = (params, namespace, duInternalIP, isAirgappedEnv) => {
    const body = createKaapiKubeadmConfigTemplateBody({
      name: params.clusterName,
      namespace,
      clusterName: params.clusterName,
      duInternalIP,
      isAirgappedEnv,
    })
    return createKubeadmConfigTemplate({ namespace, body })
  }

  // ---- Node Pools ----
  const createNodePools = async (params, namespace) => {
    for (const nodePool of params.nodePools) {
      const shortUUID = uuidv4().slice(0, 8)
      const name = `${params.clusterName}-${shortUUID}`

      const tmplBody = createKaapiOpenStackMachineTemplateBody({
        name,
        namespace,
        flavor: nodePool.flavor,
        clusterName: params.clusterName,
        storage: nodePool.storage,
        network: nodePool.network,
        subnet: nodePool.subnet,
        imageName: params.image,
        sshKeyName: params.sshKey,
      })
      await createOpenStackMachineTemplate({ namespace, body: tmplBody })

      const mdBody = createKaapiMachineDeploymentBody({
        name,
        namespace,
        clusterName: params.clusterName,
        replicas: nodePool.replicas,
        configTemplateRefName: params.clusterName,
        infrastructureRefName: name,
        k8sVersion: params.k8sVersion,
        autoscaling: nodePool.autoscaling,
        openstackCluster: params.openstackCluster,
      })
      await createMachineDeployment({ namespace, body: mdBody })
    }
  }

  // ---- Addons ----
  const createOptionalAddons = async (params, namespace) => {
    const body = createKaapiClusterAddonBody({
      clusterName: params.clusterName,
      namespace,
      addonType: 'metallb',
      version: '0.14.9',
    })
    return createClusterAddon({ namespace, body })
  }

  // ---- Redirect ----
  const redirectToClusterDetail = (clusterName) => {
    updateK8sPluginGlobalParams({ cluster: clusterName })
    onClose()
    history.push(
      routeManager.routes.kubernetes.infrastructure.cluster.path({
        cluster: clusterName,
        tab: IClusterDetailTabs.OVERVIEW,
      }),
    )
  }

  // ---- Fail helper ----
  const fail = (err, defaultMessage) => {
    setLoading(false)
    setError(
      err || {
        title: 'Error',
        message: defaultMessage,
      },
    )
    return false
  }

  // Handle OpenStack cluster creation
  const handleOpenStackClusterCreation = async () => {
    setError(null)
    setLoading(true)

    try {
      // Step 0: Validate
      if (!validateClusterParams(params)) {
        setLoading(false)
        return
      }

      // Step 1: Ensure IdentityRef secret
      const identityRes = await ensureIdentityRefSecret(params.clusterName, secrets)
      if (!identityRes?.success) {
        setLoading(false)
        setError(
          identityRes?.error || { title: 'Error', message: 'Failed to create IdentityRef secret' },
        )
        return
      }

      // Step 2: Cluster secret
      const clusterSecretRes = await createClusterSecret(params)
      if (!clusterSecretRes?.success) {
        setLoading(false)
        setError(
          clusterSecretRes?.error || { title: 'Error', message: 'Failed to create cluster secret' },
        )
        return
      }

      // Step 3: Infra (OpenStackCluster, HCP, Cluster)
      const infraRes = await createClusterInfrastructure(params, namespace)
      if (!infraRes?.success) {
        setLoading(false)
        setError(infraRes?.error || { title: 'Error', message: infraRes?.message })
        return
      }

      // Step 4: Config templates
      try {
        await createConfigTemplates(params, namespace, duInternalIP, isAirgappedEnv)
      } catch (err) {
        setLoading(false)
        setError({ title: 'Error', message: 'Failed to create config templates' })
        return
      }

      // Step 5: Node pools
      try {
        await createNodePools(params, namespace)
      } catch (err) {
        setLoading(false)
        setError({ title: 'Error', message: 'Failed to create node pools' })
        return
      }

      // Step 6: Optional addons
      if (params.metallb) {
        try {
          const {
            success: createClusterAddonSuccess,
            error: createClusterAddonError,
          } = await createOptionalAddons(params, namespace)

          if (!createClusterAddonSuccess) {
            setLoading(false)
            setError(
              createClusterAddonError || {
                title: 'Error',
                message: 'Failed to create ClusterAddon',
              },
            )
            return
          }
        } catch (err) {
          setLoading(false)
          setError({ title: 'Error', message: 'Failed to create ClusterAddon' })
          return
        }
      }

      // Step 7: Redirect
      if (!hasError) {
        try {
          await redirectToClusterDetail(params.clusterName)
        } catch (err) {
          setLoading(false)
          setError({ title: 'Error', message: 'Failed to redirect to cluster detail page' })
          return
        }
      }
    } finally {
      setLoading(false)
    }
  }

  // Reset form callback
  const resetForm = useCallback(() => {
    setError(null)
    setLoading(false)
  }, [])

  const submitting =
    creatingOpenStackCluster ||
    creatingHostedControlPlane ||
    creatingCluster ||
    creatingKubeadmConfigTemplate ||
    creatingOpenStackMachineTemplate ||
    creatingMachindeDeployment ||
    creatingClusterAddon ||
    loading

  const steps: ModalStep[] = [
    {
      stepId: 1,
      headerTitle: 'Cluster Architecture',
      description:
        'Select the compute infrastructure for this cluster. Using Virtualized Clusters enables integrated management of node pools, cluster autoscaler, storage and load balancers.',
      component: (
        <div className={classes.formSection}>
          <ClusterNameField
            params={params}
            getParamsUpdater={getParamsUpdater}
            clusters={clusters}
          />
          <ClusterArchitectureFieldsOpenStack
            params={params}
            getParamsUpdater={getParamsUpdater}
            setError={setError}
          />
        </div>
      ),
      stepValidator: () => {
        if (isAirgappedEnv && !duInternalIP) {
          setError({
            title: 'Error',
            message:
              'Something went wrong, please try again. Tenant may not be configured correctly',
          })
          return {
            success: false,
          }
        }
        return { success: true }
      },
    },
    {
      stepId: 2,
      headerTitle: 'Servers, Storage and Network',
      description:
        'Configure the compute resources for this cluster. Using Virtualized Clusters enables integrated management of node pools, cluster autoscaler, storage and load balancers.',
      component: (
        <div className={classes.formSection}>
          <ServerConfigurationFields
            id="nodePools"
            params={params}
            getParamsUpdater={getParamsUpdater}
            nodePoolErrors={nodePoolErrors}
            setIsGPUFlavorSelected={setIsGPUFlavorSelected}
          />
        </div>
      ),
      stepValidator: () => {
        const nodePoolErrors = validateNodePools(params.nodePools)
        setNodePoolErrors(nodePoolErrors)
        if (!isNilOrEmpty(nodePoolErrors)) {
          return {
            success: false,
          }
        }
        return { success: true }
      },
    },
    {
      stepId: 3,
      headerTitle: 'Configure your Cluster',
      description:
        'Configure your cluster’s Kubernetes version, your GPUs partitioning strategy (if applicable), optional Kubernetes flags, and add-ons.',
      component: (
        <div className={classes.formSection}>
          <ClusterConfigFields
            params={params}
            getParamsUpdater={getParamsUpdater}
            isOpenStackCluster
            isGPUFlavorSelected={isGPUFlavorSelected}
          />
        </div>
      ),
      stepValidator: () => {
        if (!params?.image || !params?.k8sVersion) {
          return {
            success: false,
          }
        }
        return { success: true }
      },
    },
  ]

  return (
    <StepperModalForm
      steps={steps}
      title="Deploy A New Cluster"
      maxWidth={876}
      open={true}
      onClose={onClose}
      onSubmit={handleOpenStackClusterCreation}
      error={error}
      resetError={() => setError(null)}
      submitting={submitting}
      ValidatedFormComponent={ValidatedForm as any}
      params={params}
      customWarningComponent={
        <ServiceUnhealthyInfo
          action="Cluster Creation"
          hypervisor
          imageLibrary
          blockStorage
          network
        />
      }
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
  formSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
  },
}))

export default ClusterDeploymentForm
